import pdfMake from 'pdfmake/build/pdfmake';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { getCorporativoInfo, getDataBase64 } from '../../lib/Util';
import { IReportHoursQuantified } from '../../system/reporting/reporte-horas-cuantificadas';
import { DateTime } from 'luxon';
import { Company } from '../../system/company/Company';
import { t } from 'i18next';
(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;

// ---------------- HELPERS ----------------
const chunk = <T>(arr: T[], size: number): T[][] => {
  const res: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    res.push(arr.slice(i, i + size));
  }
  return res;
};

const groupBy = <T extends Record<string, any>>(arr: T[], key: string) => {
  return arr.reduce(
    (acc, item) => {
      acc[item[key]] ??= [];
      acc[item[key]].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
};

// ---------------- MAIN ----------------
export const exportQuantifiedHoursPdf = async ({
  period,
  getFilteredData,
  company,
  node,
}: {
  period: DateTime | undefined;
  node: string;
  company: Company | null;
  getFilteredData: () => Promise<IReportHoursQuantified[]>;
}) => {
  const corporativo = getCorporativoInfo();
  const IMAGE_LOGO_BASE64 = getDataBase64();

  const logoBase64 = IMAGE_LOGO_BASE64.startsWith('data:image') ? IMAGE_LOGO_BASE64 : `data:image/png;base64,${IMAGE_LOGO_BASE64}`;

  if (!corporativo || !period) return;

  const data = await getFilteredData();

  // ---------------- 1. ORDENAR RESPETANDO JERARQUÍA ----------------
  const hierarchy: Record<string, Record<string, Record<string, IReportHoursQuantified[]>>> = {};

  data.forEach((row) => {
    const area = row.principal_Area || 'Sin área';
    const concepto = row.Concepto || 'Sin concepto';
    const proyecto = row.Nombre_de_Servicio_o_Proyecto || 'Sin proyecto';

    hierarchy[area] ??= {};
    hierarchy[area][concepto] ??= {};
    hierarchy[area][concepto][proyecto] ??= [];

    hierarchy[area][concepto][proyecto].push(row);
  });

  // ---------------- 2. APLANAR EN ORDEN CORRECTO ----------------
  const orderedRows: {
    area: string;
    concepto: string;
    proyecto: string;
    row: IReportHoursQuantified;
  }[] = [];

  Object.entries(hierarchy).forEach(([area, conceptos]) => {
    Object.entries(conceptos).forEach(([concepto, proyectos]) => {
      Object.entries(proyectos).forEach(([proyecto, rows]) => {
        rows.forEach((row) => {
          orderedRows.push({ area, concepto, proyecto, row });
        });
      });
    });
  });

  // ---------------- 3. PAGINAR SIN ROMPER ORDEN ----------------
  const pages = chunk(orderedRows, 43);

  const content: Content[] = [];

  pages.forEach((pageRows, index) => {
    const body: pdfMake.TableCell[][] = [
      [
        { text: t('Área').toString(), style: 'tableHeader' },
        { text: t('Concepto').toString(), style: 'tableHeader' },
        { text: t('Proyecto / Servicio').toString(), style: 'tableHeader' },
        { text: t('Detalle Actividad').toString(), style: 'tableHeader' },
        { text: t('Horas').toString(), style: 'tableHeader' },
      ],
    ];

    const areaGroups = groupBy(pageRows, 'area');
    

    Object.entries(areaGroups).forEach(([area, areaRows]) => {
      const conceptoGroups = groupBy(areaRows, 'concepto');
      let areaAdded = false;

      Object.entries(conceptoGroups).forEach(([concepto, conceptoRows]) => {
        const proyectoGroups = groupBy(conceptoRows, 'proyecto');
        let conceptoAdded = false;

        Object.entries(proyectoGroups).forEach(([proyecto, proyectoRows]) => {
          let proyectoAdded = false;

          proyectoRows.forEach(({ row }) => {
            const currentRow: pdfMake.TableCell[] = [];

            // ---------------- AREA ----------------
            if (!areaAdded) {
              currentRow.push({
                text: area,
                rowSpan: areaRows.length,
                bold: true,
                alignment: 'center',
                margin: [0, 1],
              });
              areaAdded = true;
            } else {
              currentRow.push({});
            }

            // ---------------- CONCEPTO ----------------
            if (!conceptoAdded) {
              currentRow.push({
                text: concepto,
                rowSpan: conceptoRows.length,
                alignment: 'center',
                margin: [0, 1],
              });
              conceptoAdded = true;
            } else {
              currentRow.push({});
            }

            // ---------------- PROYECTO ----------------
            if (!proyectoAdded) {
              currentRow.push({
                text: proyecto,
                rowSpan: proyectoRows.length,
                alignment: 'center',
                margin: [0, 1],
              });
              proyectoAdded = true;
            } else {
              currentRow.push({});
            }

            // ---------------- DETALLE ----------------
            currentRow.push(
              { text: row.act_nombre },
              {
                text: parseFloat(row.Horas_Distribuido).toFixed(4),
                alignment: 'right',
              },
            );

            body.push(currentRow);
          });
        });
      });
    });

    content.push({
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: ['auto', 'auto', 'auto', '*', 30],
        body,
      },
      layout: {
        fillColor: (rowIndex: number) => (rowIndex === 0 ? '#0078D4' : null),
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#d1d5db',
        vLineColor: () => '#d1d5db',
        paddingLeft: () => 4,
        paddingRight: () => 4,
        paddingTop: () => 3,
        paddingBottom: () => 3,
      },
    });

    if (index < pages.length - 1) {
      content.push({
        text: '',
        pageBreak: 'after',
      });
    }
  });

  console.log(pages.length);
  

  // ---------------- HEADER ----------------
  const headerStack: Content[] = [
    {
      text: `${company ? company.getName() : corporativo.empp_nombre} ${period.toFormat('yyyy')}`,
      alignment: 'center',
      color: '#0078D4',
      fontSize: 14,
    },
  ];

  if (node) {
    headerStack.push({
      text: data[0]?.principal_Area ?? '',
      alignment: 'center',
      fontSize: 12,
      margin: [0, 5, 0, 0],
    });
  }

  headerStack.push({
    text: period.setLocale('es').toFormat('LLLL').toUpperCase(),
    alignment: 'center',
    color: '#0078D4',
    bold: true,
    fontSize: 18,
    margin: [0, 8, 0, 0],
  });

  // ---------------- DOC ----------------
  const docDefinition: TDocumentDefinitions = {
    pageSize: 'A4',
    pageMargins: [20, 90, 20, 40],
    header: {
      margin: [20, 15, 20, 10],
      columns: [
        { image: logoBase64, width: 100 },
        { width: '*', stack: headerStack },
      ],
    },

    footer: (currentPage, pageCount) => ({
      margin: [20, 10],
      columns: [
        {
          text: corporativo.empp_nombre,
          alignment: 'left',
          fontSize: 8,
          color: '#666',
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: 'right',
          fontSize: 8,
          color: '#666',
        },
      ],
    }),

    content,

    styles: {
      tableHeader: {
        color: 'white',
        bold: true,
        alignment: 'center',
        fontSize: 9,
      },
    },

    defaultStyle: {
      fontSize: 8,
    },
  };

  pdfMake.createPdf(docDefinition).download(`reporte-horas-cuantificadas-${period.toFormat('yyyy-MM')}.pdf`);
};
